import { useEffect, useMemo, useState } from 'react';
import { useUserStore } from '@citiguide/frontend-core';
import { useMarker } from '../hooks/useMarkers';
import { useLanguageStore } from '../store/languageStore';
import { useMarkerStore } from '../store/markerStore';
import { MapContainer, UserMarker } from '@citiguide/frontend-core';
import MarkerCluster from '../components/map/MarkerCluster';
import MapAutoCenter from '../components/map/MapAutoCenter';
import MarkerModal from '../components/markers/MarkerModal';
import MainMenu from '../components/menu/MainMenu';
import RouteCalculator from '../components/routes/RouteCalculator';
import { useGeolocation } from '../hooks/useGeolocation';
import { getURLParams } from '../utils/urlParams';
import { useCurationStore, hasActiveFilters, toMarkerQueryParams } from '../store/curationStore';
import EmptyFilterNotice from '../components/map/EmptyFilterNotice';
import ChatRoot from '../components/chat/ChatRoot';
import SharedPlanBanner from '../components/plans/SharedPlanBanner';
import MyPlansButton from '../components/plans/MyPlansButton';
import { useChatStatus } from '../hooks/useChatStatus';
import { usePlansStore } from '../store/plansStore';
import { createLogger } from '../utils/logger';
import { useViewportMarkers } from '@citiguide/frontend-core';
import { getViewportMarkers } from '../services/markerService';
import '../styles/map.css';

const log = createLogger('MapPage');

export default function MapPage() {
  log.debug('Component rendering');
  
  const { currentLanguage, setLanguage } = useLanguageStore();
  const [urlParams, setUrlParams] = useState(getURLParams());
  
  log.debug('URL params:', urlParams);
  log.debug('Current language:', currentLanguage);
  
  // Visitor's filter selection (initialised from the URL) is sent to the server with every marker fetch
  const curated = useCurationStore((s) => s.state);
  const clearFilters = useCurationStore((s) => s.clearFilters);
  const hydrateFromURL = useCurationStore((s) => s.hydrateFromURL);
  // My plans appears only where the chat is on (or the visitor already has saved plans): with the tenant
  // flag off the map is today's UI.
  const { data: chatStatus } = useChatStatus();
  const hasSavedPlans = usePlansStore((s) => s.plans.length > 0);

  // The visitor's position exists only after the browser permission was granted (useGeolocation below).
  const userLocation = useUserStore((s) => s.location);
  const lat = userLocation?.[0];
  const lng = userLocation?.[1];
  const extraParams = useMemo(
    () => toMarkerQueryParams(curated, lat !== undefined && lng !== undefined ? [lat, lng] : null),
    [curated, lat, lng],
  );

  // Viewport-based marker loading with server-side clustering
  const { markers, clusters, isLoading, error, totalCount } = useViewportMarkers({
    fetchFn: getViewportMarkers,
    language: currentLanguage,
    extraParams,
  });

  log.debug('Markers state:', {
    isLoading,
    hasError: !!error,
    error: error ? (error as { message?: string })?.message || error : null,
    markersCount: markers?.length || 0,
    clustersCount: clusters?.length || 0,
    totalCount,
  });
  
  const { setHighlightedMarker, selectedMarkerId, setSelectedMarker, routeType, requestRoute, clearRoute } = useMarkerStore();
  useGeolocation(true);
  const { data: selectedMarker } = useMarker(selectedMarkerId);

  // Handle URL parameters
  useEffect(() => {
    log.debug('Handling URL parameters');
    const params = getURLParams();
    log.debug('URL params from getURLParams:', params);
    setUrlParams(params);
    if (params.language) {
      setLanguage(params.language as 'en' | 'fi' | 'se' | 'es');
    }
    if (params.markerId) {
      setHighlightedMarker(params.markerId);
    }
    hydrateFromURL();
  }, [setLanguage, setHighlightedMarker, hydrateFromURL]);

  if (isLoading) {
    log.debug('Rendering loading state');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading map...</p>
      </div>
    );
  }

  if (error) {
    log.error('Map load error:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorResponse = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
    const errorDetails = errorResponse || (error as { response?: unknown })?.response || error;
    
    // Check if it's a subdomain not found error
    if (errorResponse?.error === 'Subdomain not found' || errorResponse?.message?.includes('subdomain')) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px', textAlign: 'center' }}>
          <h2 style={{ color: '#333', marginBottom: '1rem' }}>Subdomain Not Found</h2>
          <p style={{ color: '#666', marginBottom: '0.5rem' }}>{errorResponse?.message || 'The requested subdomain does not exist or is not configured.'}</p>
          <p style={{ fontSize: '0.9rem', color: '#999' }}>
            Please check the URL or contact the administrator.
          </p>
        </div>
      );
    }
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
        <p style={{ color: 'red', marginBottom: '10px' }}>Error loading map data</p>
        <p style={{ fontSize: '12px', color: '#666' }}>{errorMessage}</p>
        <pre style={{ fontSize: '10px', color: '#999', marginTop: '10px', maxWidth: '600px', overflow: 'auto' }}>
          {JSON.stringify(errorDetails, null, 2)}
        </pre>
      </div>
    );
  }
  
  log.debug('Rendering map with markers:', markers?.length || 0);

  const handleRouteRequest = (type: 'walking' | 'driving') => {
    requestRoute(type);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <MainMenu />
      {(chatStatus || hasSavedPlans) && <MyPlansButton />}
      <MapContainer>
        <MapAutoCenter />
        {(markers.length > 0 || clusters.length > 0) && (
          <MarkerCluster markers={markers} clusters={clusters} />
        )}
        <UserMarker />
        {selectedMarker && routeType && selectedMarker.location?.coordinates && (
          <RouteCalculator
            markerLat={selectedMarker.location.coordinates[1]}
            markerLng={selectedMarker.location.coordinates[0]}
            routeType={routeType}
            onRouteCalculated={(distance, duration) => {
              log.info(`Route calculated: ${distance.toFixed(2)} km, ${duration.toFixed(0)} min`);
            }}
            onRouteError={(err) => {
              log.error('Route calculation error:', err);
            }}
          />
        )}
      </MapContainer>
      {hasActiveFilters(curated) && totalCount === 0 && (
        <EmptyFilterNotice onClear={clearFilters} />
      )}
      <SharedPlanBanner />
      <ChatRoot />
      {selectedMarker && (
        <MarkerModal
          marker={selectedMarker}
          isOpen={!!selectedMarkerId}
          onClose={() => {
            setSelectedMarker(null);
            clearRoute();
          }}
          onRouteRequest={handleRouteRequest}
        />
      )}
    </div>
  );
}

